import { useSelector, useDispatch } from "react-redux";
import {
  useProjectCreateStore,
  selectDesignSystem,
} from "@/store/zustand/projectCreateStore";
import { useState } from "react";
import { useActiveMainCanvas } from "@/utils/utils";
import { setDSPrompt } from "@/store/supabaseSlice";
import { cn } from "@/lib/utils";
import TempoModal from "@/components/Core/TempoModal";
import { useAuth } from "@clerk/clerk-react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { useSupabaseStore } from "@/store/zustand/supabaseStore";

const PromptEditor = () => {
  const dispatch = useDispatch();
  const { getToken } = useAuth();

  const selectedDesignSystem = useProjectCreateStore(selectDesignSystem);

  const dsPrompt = useSelector((state: any) => state.supabaseStore.dsPrompt);

  const supabaseClient = useSupabaseStore((state) => state.supabaseClient);

  const [currentDSPrompt, setCurrentDSPrompt] = useState(dsPrompt);

  const mainCanvas = useActiveMainCanvas();

  const canvasId = mainCanvas?.id;

  const projectId = mainCanvas?.project_id;

  const [showResetPromptModal, setShowResetPromptModal] = useState(false);
  const [isModalLoading, setIsModalLoading] = useState(false);
  const [isGeneratingDocumentation, setIsGeneratingDocumentation] =
    useState(false);

  const generateDocumentation = async () => {
    const token = await getToken();

    setIsGeneratingDocumentation(true);
    try {
      const res = await axios.post(
        `${
          import.meta.env.VITE_BACKEND_BASE_URL
        }/canvases/${canvasId}/ai/vercel-ai/generateDesignSystemDocumentation`,
        {},
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = res.data.data.payload.documentation;

      setCurrentDSPrompt(data);
    } catch (error) {
      console.error(error);
    } finally {
      setIsGeneratingDocumentation(false);
    }
  };

  return (
    <div className="flex flex-col">
      {showResetPromptModal && (
        <TempoModal
          header="Reset to Default"
          onClose={() => setShowResetPromptModal(false)}
        >
          <div className="flex flex-col gap-6 max-w-[400px]">
            <p className="text-xs text-secondary-800">
              Are you sure you want to reset the design system prompt to
              default? You'll permanently lose all of your changes, and this
              cannot be undone.{" "}
            </p>
            <div className="flex flex-row gap-2 ml-auto">
              <Button
                variant="outline"
                size="xs"
                onClick={() => {
                  setShowResetPromptModal(false);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="xs"
                isLoading={isModalLoading}
                onClick={async () => {
                  if (!supabaseClient) return;

                  setIsModalLoading(true);

                  const { data, error } = await supabaseClient
                    .from("design_system_prompts")
                    .select("*")
                    .eq("design_system", selectedDesignSystem || "BLANK")
                    .single();

                  if (error) {
                    console.error(error);
                    return;
                  }

                  setCurrentDSPrompt(data.prompt);
                  setIsModalLoading(false);
                }}
              >
                Reset
              </Button>
            </div>
          </div>
        </TempoModal>
      )}
      <div className="flex flex-row gap-2 px-4 py-2 justify-between items-center -mb-4">
        <div className="flex flex-row gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              setShowResetPromptModal(true);
              setIsModalLoading(false);
            }}
          >
            Reset
          </Button>
          <Button
            variant="secondary"
            size="sm"
            isLoading={isGeneratingDocumentation}
            onClick={generateDocumentation}
          >
            Generate
          </Button>
        </div>
        {dsPrompt !== currentDSPrompt && (
          <div className="flex flex-row gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setCurrentDSPrompt(dsPrompt);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={async () => {
                if (!supabaseClient || !projectId) return;

                const { data, error } = await supabaseClient
                  .from("project_design_system_mapping")
                  .upsert(
                    {
                      prompt: currentDSPrompt,
                      project_id: projectId,
                    },
                    {
                      onConflict: "project_id",
                    },
                  );

                if (error) {
                  console.error(error);
                  return;
                }

                dispatch(setDSPrompt({ prompt: currentDSPrompt }));
              }}
            >
              Save
            </Button>
          </div>
        )}
      </div>
      <div className="flex flex-col w-full p-4 gap-4">
        <div className="flex flex-col gap-1">
          <p className="text-xs font-medium text-secondary-n-50">
            Override the existing design system prompt
          </p>
          <p className="text-2xs text-secondary-n-400">
            Add specific instructions, context, or documentation for the design
            system.
          </p>
        </div>
        <textarea
          value={currentDSPrompt}
          onChange={(e) => setCurrentDSPrompt(e.target.value)}
          className={cn(
            "w-full min-h-[192px] text-[12px] p-2 resize-none bg-[#1a1b1d] text-white placeholder:text-secondary-600 rounded-lg border hover:border-secondary-n-600 focus-within:border-primary-n-600",
          )}
          spellCheck="false"
          style={{
            fontFamily: "'JetBrainsMono', monospace",
          }}
          placeholder="By default, the app should use #F5F5F5 as the base colour. Use #131519 when it switches to dark mode..."
        />
      </div>
    </div>
  );
};

export default PromptEditor;
